import React, { useState, useEffect } from 'react';
import { X, Trash2, CheckSquare, Square } from 'lucide-react';
import { useConfirmation } from '../../hooks/useConfirmation';
import { useToast } from '../../hooks/useToast';
import BaseModal from '../common/BaseModal';

const DuplicateDetailsModal = ({ isOpen, onClose, duplicates, transactions, onBulkDelete }) => {
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const { confirm, ConfirmationModal } = useConfirmation();
  const { showToast, ToastContainer } = useToast();

  // Initialize selection with all duplicates when modal opens
  useEffect(() => {
    if (isOpen && duplicates.length > 0) {
      const duplicateIds = duplicates.map(d => d.duplicate_id);
      setSelectedDuplicates(new Set(duplicateIds));
    }
  }, [isOpen, duplicates]);

  const handleSelectDuplicate = (duplicateId) => {
    const newSelection = new Set(selectedDuplicates);
    if (newSelection.has(duplicateId)) {
      newSelection.delete(duplicateId);
    } else {
      newSelection.add(duplicateId);
    }
    setSelectedDuplicates(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedDuplicates.size === duplicates.length) {
      setSelectedDuplicates(new Set());
    } else {
      const duplicateIds = duplicates.map(d => d.duplicate_id);
      setSelectedDuplicates(new Set(duplicateIds));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDuplicates.size === 0) {
      showToast('Please select duplicates to delete', 'error');
      return;
    }

    const confirmed = await confirm(
      'Delete Duplicates',
      `Are you sure you want to delete ${selectedDuplicates.size} selected duplicate transaction(s)?`,
      'danger',
      'Delete',
      'Cancel'
    );
    if (!confirmed) return;

    try {
      await onBulkDelete(Array.from(selectedDuplicates));
      showToast(`Successfully deleted ${selectedDuplicates.size} duplicate transactions`, 'success');
      onClose();
    } catch (err) {
      console.error('Error deleting duplicates:', err);
      showToast(`Failed to delete duplicates: ${err.message || 'Unknown error'}`, 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTransactionById = (id) => {
    return transactions.find(t => t.id === id);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="2xl" showCloseButton={false}>
      {/* Custom Header with X button */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Duplicate Transaction Details
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Found {duplicates.length} duplicate pairs. Each pair shows the original transaction (left) and its duplicate (right).
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-750">
              <tr>
                <th className="px-3 py-3 text-left">
                    <button
                      onClick={handleSelectAll}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      title="Select all duplicates"
                    >
                      {selectedDuplicates.size === duplicates.length && duplicates.length > 0
                        ? <CheckSquare className="h-4 w-4" />
                        : <Square className="h-4 w-4" />
                      }
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Original (Keep)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duplicate (Delete)</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Differences</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {duplicates.map((duplicate, index) => {
                  const original = getTransactionById(duplicate.original_id);
                  const dup = getTransactionById(duplicate.duplicate_id);

                  if (!original || !dup) return null;

                  const hasDifferences = original.reference_id !== dup.reference_id ||
                                       original.ai_category !== dup.ai_category;

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedDuplicates.has(dup.id)}
                          onChange={() => handleSelectDuplicate(dup.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {index + 1}
                      </td>

                      {/* Original Transaction */}
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            🟢
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {original.description || 'Transaction'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(original.transaction_date)} • {formatCurrency(original.total_amount)} • ID: {original.id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Duplicate Transaction */}
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            🔴
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {dup.description || 'Transaction'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(dup.transaction_date)} • {formatCurrency(dup.total_amount)} • ID: {dup.id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Differences */}
                      <td className="px-3 py-4 text-sm">
                        {hasDifferences ? (
                          <div className="space-y-1">
                            {original.reference_id !== dup.reference_id && (
                              <div className="text-xs text-yellow-600">Ref ID</div>
                            )}
                            {original.ai_category !== dup.ai_category && (
                              <div className="text-xs text-yellow-600">Category</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-green-600">Identical</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {selectedDuplicates.size > 0 && (
            <span>{selectedDuplicates.size} of {duplicates.length} duplicates selected</span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {selectedDuplicates.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete {selectedDuplicates.size} Selected
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      <ConfirmationModal />
      <ToastContainer />
    </BaseModal>
  );
};

export default DuplicateDetailsModal;